import React from 'react';
import { Filter, Plus, X } from 'lucide-react';
import type {
  PromptSelectionCondition,
  PromptSelectionFilter,
  PromptSelectionFilterGroup,
} from '../services/api';
import type { ConditionFieldDefinition } from '../config/conditionSearch';
import {
  getConditionOperatorsForField,
  MAX_CONDITION_FILTERS,
  operatorRequiresConditionValue,
} from '../config/conditionSearch';
import { hasPromptSelectionFilter } from '../features/workflows/config';
import { formatEngineConditionValue, formatEngineFieldLabel } from '../features/workflows/presentation';
import CustomSelect from './CustomSelect';
import './WorkflowFilterEditor.css';

interface WorkflowFilterEditorProps {
  value: PromptSelectionFilter | null;
  segmentFields: ConditionFieldDefinition[];
  videoFields: ConditionFieldDefinition[];
  onChange: (value: PromptSelectionFilter | null) => void;
  editable?: boolean;
  purpose?: 'engine' | 'results';
}

type FilterLevel = 'segment' | 'video';

const makeCondition = (
  field: ConditionFieldDefinition | undefined,
  operatorValue?: string,
): PromptSelectionCondition | null => {
  if (!field) return null;
  const operators = getConditionOperatorsForField(field);
  const operator = operatorValue
    ? operators.find((candidate) => candidate.value === operatorValue)
    : operators[0];
  if (!operator) return null;
  return {
    field: field.path,
    operator: operator.value,
    type: field.type,
    ...(field.itemType ? { item_type: field.itemType } : {}),
    ...(operator.requiresValue ? { value: '' } : {}),
  };
};

const getReadableValue = (condition: PromptSelectionCondition): string => {
  if (typeof condition.value === 'boolean') return condition.value ? 'true' : 'false';
  return condition.value === undefined || condition.value === null ? '' : String(condition.value);
};

const FIELD_TYPE_LABELS = {
  string: 'Text', integer: 'Whole number', number: 'Number', boolean: 'true / false', array: 'List',
};

const GROUP_LOGIC_OPTIONS = [
  { value: 'and', label: 'All conditions (AND)' },
  { value: 'or', label: 'Any condition (OR)' },
];

const WorkflowFilterEditor: React.FC<WorkflowFilterEditorProps> = ({
  value, segmentFields, videoFields, onChange, editable = true, purpose = 'engine',
}) => {
  const enabled = hasPromptSelectionFilter(value);
  const totalConditionCount = (value?.segment?.conditions.length || 0)
    + (value?.video?.conditions.length || 0);
  const hasSegmentConditions = Boolean(value?.segment?.conditions.length);
  const hasVideoConditions = Boolean(value?.video?.conditions.length);
  const hasAnyFields = segmentFields.length > 0 || videoFields.length > 0;

  const updateGroup = (level: FilterLevel, group?: PromptSelectionFilterGroup) => {
    const next: PromptSelectionFilter = { level_logic: 'and', ...value };
    if (group) next[level] = group;
    else delete next[level];
    onChange(hasPromptSelectionFilter(next) ? next : null);
  };

  const enableFilter = () => {
    const segment = makeCondition(segmentFields[0]);
    const video = makeCondition(videoFields[0]);
    if (segment) {
      onChange({ segment: { logic: 'and', conditions: [segment] }, level_logic: 'and' });
    } else if (video) {
      onChange({ video: { logic: 'and', conditions: [video] }, level_logic: 'and' });
    }
  };

  const renderGroup = (
    level: FilterLevel,
    title: string,
    description: string,
    fields: ConditionFieldDefinition[],
  ) => {
    const group = value?.[level];
    const conditions = group?.conditions || [];
    if (!editable && conditions.length === 0) return null;

    const setCondition = (index: number, condition: PromptSelectionCondition | null) => {
      if (!condition) return;
      updateGroup(level, {
        logic: group?.logic || 'and',
        conditions: conditions.map((current, conditionIndex) => conditionIndex === index ? condition : current),
      });
    };

    const addCondition = () => {
      const condition = makeCondition(fields[0]);
      if (!condition) return;
      updateGroup(level, { logic: group?.logic || 'and', conditions: [...conditions, condition] });
    };

    return (
      <section className="workflow-filter-group" aria-label={`${title} filters`}>
        <div className="workflow-filter-group-header">
          <div><h5>{title}</h5><p>{description}</p></div>
          {conditions.length > 1 && (editable ? (
            <CustomSelect
              className="workflow-filter-logic-select"
              value={group?.logic || 'and'}
              options={GROUP_LOGIC_OPTIONS}
              onChange={(logic) => updateGroup(level, { logic: logic === 'or' ? 'or' : 'and', conditions })}
              ariaLabel={`${title} condition logic`}
            />
          ) : (
            <span className="workflow-filter-status">{group?.logic === 'or' ? 'Any condition (OR)' : 'All conditions (AND)'}</span>
          ))}
        </div>

        {conditions.length === 0 ? (
          <div className="workflow-filter-empty">
            <span>{fields.length === 0
              ? purpose === 'results'
                ? `No ${level === 'video' ? 'video-level' : 'segment'} fields are available in this extraction.`
                : `Add ${level === 'video' ? 'whole-video' : 'segment'} fields in Extraction fields to use these conditions.`
              : `No ${level === 'video' ? 'whole-video' : 'segment'} conditions yet.`}</span>
            {editable && fields.length > 0 && totalConditionCount < MAX_CONDITION_FILTERS && (
              <button type="button" className="workflow-filter-add" onClick={addCondition}>
                <Plus size={14} aria-hidden="true" /> Add condition
              </button>
            )}
          </div>
        ) : (
          <div className="workflow-filter-conditions">
            {conditions.map((condition, index) => {
              const field = fields.find((candidate) => candidate.path === condition.field)
                || (!editable ? {
                  path: condition.field, label: condition.field, type: condition.type, itemType: condition.item_type,
                } : undefined);
              const operators = field ? getConditionOperatorsForField(field) : [];
              const requiresValue = field ? operatorRequiresConditionValue(field.type, condition.operator) : true;
              const isLength = condition.operator.startsWith('length_');
              const valueType = isLength ? 'integer' : field?.type === 'array' ? field.itemType : field?.type;
              const isBoolean = valueType === 'boolean';
              const isNumeric = valueType === 'integer' || valueType === 'number';
              const readableValue = getReadableValue(condition);
              const operatorLabel = operators.find((operator) => operator.value === condition.operator)?.label
                || 'Choose an operator';
              const displayValue = formatEngineConditionValue(condition);

              return (
                <React.Fragment key={`${level}-${index}`}>
                  {index > 0 && <div className="workflow-filter-join"><span>{group?.logic === 'or' ? 'OR' : 'AND'}</span></div>}
                  {editable ? (
                    <div className="workflow-filter-condition">
                      <div className="workflow-filter-condition-header">
                        <span>Condition {index + 1}</span>
                        {field && <span className="workflow-filter-type">{FIELD_TYPE_LABELS[field.type]}</span>}
                        <button
                          type="button"
                          className="workflow-filter-remove"
                          onClick={() => {
                            const nextConditions = conditions.filter((_, conditionIndex) => conditionIndex !== index);
                            updateGroup(level, nextConditions.length > 0
                              ? { logic: group?.logic || 'and', conditions: nextConditions } : undefined);
                          }}
                          aria-label={`Remove ${title.toLowerCase()} condition ${index + 1}`}
                          title="Remove condition"
                        ><X size={14} aria-hidden="true" /></button>
                      </div>
                      <div className={`workflow-filter-condition-fields${requiresValue ? '' : ' no-value'}`}>
                        <div className="workflow-filter-field">
                          <span>Field</span>
                          <CustomSelect
                            value={condition.field}
                            options={fields.map((option) => ({ value: option.path, label: formatEngineFieldLabel(option.label) }))}
                            onChange={(path) => {
                              if (path !== condition.field) {
                                setCondition(index, makeCondition(fields.find((candidate) => candidate.path === path)));
                              }
                            }}
                            placeholder="Choose a field"
                            ariaLabel={`${title} condition ${index + 1} field`}
                            ariaInvalid={!field}
                          />
                        </div>
                        <div className="workflow-filter-field">
                          <span>Operator</span>
                          <CustomSelect
                            value={condition.operator}
                            options={operators}
                            onChange={(operator) => {
                              if (operator === condition.operator) return;
                              const next = makeCondition(field, operator);
                              if (next && requiresValue && Object.prototype.hasOwnProperty.call(next, 'value')
                                && isLength === operator.startsWith('length_')) {
                                next.value = condition.value;
                              }
                              setCondition(index, next);
                            }}
                            placeholder="Choose an operator"
                            disabled={!field}
                            ariaLabel={`${title} condition ${index + 1} operator`}
                          />
                        </div>
                        {requiresValue && (
                          <div className="workflow-filter-field">
                            <span>{isLength ? 'Number of items' : 'Value'}</span>
                            {isBoolean ? (
                              <CustomSelect
                                value={readableValue}
                                options={[{ value: 'true', label: 'true' }, { value: 'false', label: 'false' }]}
                                onChange={(selected) => setCondition(index, { ...condition, value: selected === 'true' })}
                                placeholder="Choose true or false"
                                ariaLabel={`${title} condition ${index + 1} value`}
                              />
                            ) : (
                              <input
                                type={isNumeric ? 'number' : 'text'}
                                min={isLength ? 0 : undefined}
                                step={isNumeric ? valueType === 'integer' ? 1 : 'any' : undefined}
                                value={readableValue}
                                onChange={(event) => setCondition(index, { ...condition, value: event.target.value })}
                                placeholder={isNumeric ? 'Enter a number' : 'Enter text'}
                                disabled={!field}
                                aria-label={`${title} condition ${index + 1} value`}
                              />
                            )}
                          </div>
                        )}
                      </div>
                      {field?.description && <p className="workflow-filter-field-help">{field.description}</p>}
                      {!field && <p className="workflow-filter-field-help error">This field is no longer in the schema. Choose another field or remove this condition.</p>}
                    </div>
                  ) : (
                    <div className="workflow-filter-condition readonly">
                      <span className="workflow-filter-field-chip" title={condition.field}>{formatEngineFieldLabel(field?.label || condition.field)}</span>
                      <span>{operatorLabel.toLowerCase()}</span>
                      {requiresValue && <span className="workflow-filter-value-chip">{displayValue}</span>}
                    </div>
                  )}
                </React.Fragment>
              );
            })}
            {editable && totalConditionCount < MAX_CONDITION_FILTERS && (
              <button type="button" className="workflow-filter-add" onClick={addCondition} disabled={fields.length === 0}>
                <Plus size={14} aria-hidden="true" /> Add condition
              </button>
            )}
          </div>
        )}
      </section>
    );
  };

  return (
    <div className="workflow-filter-editor">
      <div className="workflow-filter-heading">
        <div className="workflow-filter-title">
          <Filter size={16} aria-hidden="true" />
          <div><h4>Choose what becomes a match</h4><p>Apply conditions after extraction. All extracted results remain available.</p></div>
        </div>
        {editable ? (
          <CustomSelect
            className="workflow-filter-output-select"
            value={enabled ? 'filtered' : 'all'}
            options={[{ value: 'all', label: purpose === 'results' ? 'No additional conditions' : 'All extracted results' }, { value: 'filtered', label: 'Apply filters' }]}
            onChange={(mode) => {
              if (mode === 'all') onChange(null);
              else if (!enabled) enableFilter();
            }}
            disabled={!enabled && !hasAnyFields}
            ariaLabel="Output selection"
          />
        ) : (
          <span className={`workflow-filter-status ${enabled ? 'enabled' : ''}`}>{enabled ? 'Filtered output' : 'All extracted results'}</span>
        )}
      </div>

      {!hasAnyFields && <p className="workflow-filter-notice">{purpose === 'results'
        ? 'Filter fields will appear when extracted results are available.'
        : 'Add text, number, boolean, or list fields in Extraction fields before configuring filters.'}</p>}
      {!enabled && hasAnyFields && <p className="workflow-filter-notice">{purpose === 'results'
        ? 'No additional conditions. Search and the selected result scope can still narrow this view.'
        : 'No filters applied. This engine returns all extracted results.'}</p>}

      {enabled && (
        <div className="workflow-filter-body">
          {editable && <div className="workflow-filter-limit"><span>Build conditions from the engine’s extracted fields.</span><span className="workflow-filter-status">{totalConditionCount} / {MAX_CONDITION_FILTERS} conditions</span></div>}
          {renderGroup('segment', 'Segment conditions', 'Select individual segments whose fields match.', segmentFields)}
          {hasSegmentConditions && hasVideoConditions && (
            <div className="workflow-filter-level-logic">
              <div className="workflow-filter-level-logic-heading">
                <span>Combine the two levels</span>
                {editable ? (
                  <CustomSelect
                    value={value?.level_logic || 'and'}
                    options={[
                      { value: 'and', label: 'Both levels must match (AND)' },
                      { value: 'or', label: 'Either level may match (OR)' },
                    ]}
                    onChange={(logic) => onChange({ ...value!, level_logic: logic === 'or' ? 'or' : 'and' })}
                    ariaLabel="Combine segment and video conditions"
                  />
                ) : (
                  <span className="workflow-filter-status enabled">{value?.level_logic === 'or' ? 'Either level (OR)' : 'Both levels (AND)'}</span>
                )}
              </div>
              <p>{value?.level_logic === 'or'
                ? 'Select matching videos and matching segments independently. A video match does not select all of its segments.'
                : 'Select a video and its matching segments only when the video matches and at least one segment matches.'}</p>
            </div>
          )}
          {renderGroup('video', 'Video conditions', purpose === 'results' && !hasSegmentConditions
            ? 'Keep this video’s results only when its video-level fields match.'
            : 'Select whole videos using their video-level fields.', videoFields)}
          {editable && <p className="workflow-filter-notice">
            {hasVideoConditions
              ? `Images can match segment conditions only.${value?.level_logic !== 'or' && hasSegmentConditions ? ' With both levels required, images do not match.' : ''}`
              : 'Segment matches include their parent media for context. Images use the same segment conditions.'}
            {' '}No matches is a successful result.
          </p>}
        </div>
      )}
    </div>
  );
};

export default WorkflowFilterEditor;
